import axios from 'axios';
import createSignedRequest from './createSignedRequest';
import createUnsignedRequest from './createUnsignedRequest';

const { apiURL } = require('./apiURL');

const Types = {
  SET: 'AGREEMENTS_SET',
  LOADING: 'AGREEMENTS_LOADING',
  ERROR: 'AGREEMENTS_ERROR',
  CLEAR: 'AGREEMENTS_CLEAR',
  CREATE_RESULT: 'AGREEMENTS_CREATE_RESULT',
  RESEND_RESULT: 'AGREEMENTS_RESEND_RESULT',
  VOID_RESULT: 'AGREEMENTS_VOID_RESULT',
  SIGNED_PDF: 'AGREEMENTS_SIGNED_PDF',
  PRODUCTS: 'AGREEMENTS_PRODUCTS',
};

const setLoading = (key, value) => ({
  type: Types.LOADING,
  key,
  value,
});

const setError = (error) => ({
  type: Types.ERROR,
  error,
});

/** @param {{ agreements: object[], cancelEligibility?: object, _raw?: object }} payload */
const setAgreements = (payload) => ({
  type: Types.SET,
  payload,
});

/**
 * Fetches all agreement rows for the org (agreement flow + legacy PandaDoc).
 * @param {string} [organisationId] — when set, used as `Organisation` header (e.g. admin context).
 */
const getMyAgreements = (organisationId) => {
  return async (dispatch, getState) => {
    const state = getState();
    const org =
      organisationId != null && organisationId !== '' ? String(organisationId) : String(state.currentOrganisation);
    if (!org) return;

    dispatch(setLoading('list', true));
    dispatch(setError(null));
    try {
      const response = await axios(
        await createSignedRequest('GET', apiURL + '/my/agreements', null, { Organisation: org })
      );
      const data = response.data || {};
      dispatch(
        setAgreements({
          agreements: data.agreements || [],
          cancelEligibility: data.cancelEligibility,
          listOrganisationId: org,
          _raw: data,
        })
      );
      return data;
    } catch (e) {
      if (e?.response?.status === 404) {
        dispatch(
          setAgreements({
            agreements: [],
            cancelEligibility: undefined,
            listOrganisationId: org,
            _raw: { flowDisabled: true },
          })
        );
        return { agreements: [], flowDisabled: true };
      }
      const msg = e?.response?.data?.message || e?.response?.data?.code || e.message;
      console.log('getMyAgreements Error:', e);
      dispatch(setError(msg));
      return null;
    } finally {
      dispatch(setLoading('list', false));
    }
  };
};

/**
 * Creates a commercial order form; sends agreement invite email.
 * @param {object} body — `POST /my/agreements` body
 */
const createAgreement = (body) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (!state.currentOrganisation) return null;

    dispatch(setLoading('create', true));
    dispatch(setError(null));
    try {
      const response = await axios(
        await createSignedRequest('POST', apiURL + '/my/agreements', JSON.stringify(body), {
          Organisation: String(state.currentOrganisation),
        })
      );
      const data = response.data;
      dispatch({ type: Types.CREATE_RESULT, payload: data });
      return { ok: true, data };
    } catch (e) {
      const errBody = e?.response?.data;
      const msg = errBody?.message || errBody?.code || e.message;
      dispatch(setError(msg));
      return { ok: false, error: errBody, message: msg };
    } finally {
      dispatch(setLoading('create', false));
    }
  };
};

const resolveAgreementOrgHeader = (state, organisationId) => {
  const explicit =
    organisationId != null && organisationId !== ''
      ? String(organisationId)
      : state.agreements?.listOrganisationId;
  return explicit || (state.currentOrganisation ? String(state.currentOrganisation) : null);
};

const resendAgreement = (id, body = {}, organisationId) => {
  return async (dispatch, getState) => {
    const state = getState();
    const org = resolveAgreementOrgHeader(state, organisationId);
    if (!org) return null;

    dispatch(setLoading('resend', true));
    dispatch(setError(null));
    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          apiURL + '/my/agreements/' + encodeURIComponent(id) + '/resend',
          JSON.stringify(body),
          { Organisation: org }
        )
      );
      const data = response.data;
      dispatch({ type: Types.RESEND_RESULT, payload: { id, data } });
      return { ok: true, data };
    } catch (e) {
      const errBody = e?.response?.data;
      const msg = errBody?.message || errBody?.code || e.message;
      dispatch(setError(msg));
      return { ok: false, error: errBody, message: msg };
    } finally {
      dispatch(setLoading('resend', false));
    }
  };
};

const voidAgreement = (id, body = {}, organisationId) => {
  return async (dispatch, getState) => {
    const state = getState();
    const org = resolveAgreementOrgHeader(state, organisationId);
    if (!org) return null;

    dispatch(setLoading('void', true));
    dispatch(setError(null));
    try {
      await axios(
        await createSignedRequest(
          'POST',
          apiURL + '/my/agreements/' + encodeURIComponent(id) + '/void',
          JSON.stringify(body),
          { Organisation: org }
        )
      );
      dispatch({ type: Types.VOID_RESULT, payload: { id } });
      await dispatch(getMyAgreements(org));
      return { ok: true };
    } catch (e) {
      const errBody = e?.response?.data;
      const msg = errBody?.message || errBody?.code || e.message;
      dispatch(setError(msg));
      return { ok: false, error: errBody, message: msg };
    } finally {
      dispatch(setLoading('void', false));
    }
  };
};

/**
 * Full contract row for admin edit / detail (includes perReportFeeVal, endClientDefaultPlanType).
 * Prefer over GET /my/agreements list items, which omit consultant billing fields.
 * @param {string} id
 * @param {string} [organisationId]
 * @returns {Promise<object|null>}
 */
const getAgreementContract = (id, organisationId) => {
  return async (dispatch, getState) => {
    const state = getState();
    const org = resolveAgreementOrgHeader(state, organisationId);
    if (!org || !id) return null;

    dispatch(setLoading('contract', true));
    try {
      const response = await axios(
        await createSignedRequest('GET', apiURL + '/my/contract/' + encodeURIComponent(id), null, {
          Organisation: org,
        })
      );
      return response.data?.contract || null;
    } catch (e) {
      console.log('getAgreementContract Error:', e);
      return null;
    } finally {
      dispatch(setLoading('contract', false));
    }
  };
};

/**
 * @returns {Promise<string|null>} presigned URL
 */
/**
 * @returns {Promise<{ url: string|null, error?: string }>}
 */
const getAgreementSignedPdfUrl = (id, organisationId) => {
  return async (dispatch, getState) => {
    const state = getState();
    const org = resolveAgreementOrgHeader(state, organisationId);
    if (!org) return { url: null, error: 'missing_organisation' };

    dispatch(setLoading('signedPdf', true));
    try {
      const response = await axios(
        await createSignedRequest('GET', apiURL + '/my/agreements/' + encodeURIComponent(id) + '/signed-pdf', null, {
          Organisation: org,
        })
      );
      const url = response.data?.url || null;
      if (url) dispatch({ type: Types.SIGNED_PDF, payload: { id, url } });
      return { url, error: url ? undefined : 'pdf_not_available' };
    } catch (e) {
      const code = e?.response?.data?.error || e?.response?.data?.code;
      console.log('getAgreementSignedPdfUrl Error:', e);
      return { url: null, error: code || 'pdf_fetch_failed' };
    } finally {
      dispatch(setLoading('signedPdf', false));
    }
  };
};

/**
 * Must run before any Stripe `cancel_at_period_end` call when a committed term may apply.
 * @param {object} [options]
 * @param {boolean} [options.confirm] — default true
 * @param {string} [options.reason]
 */
const requestSubscriptionCancel = (options = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const org = state.currentOrganisation;
    if (!org) return { ok: false, error: { message: 'No organisation' } };

    const body = { confirm: true, reason: options.reason, ...options };
    if (body.confirm === undefined) body.confirm = true;

    try {
      const response = await axios(
        await createSignedRequest('POST', apiURL + '/my/subscription/cancel-request', JSON.stringify(body), {
          Organisation: String(org),
        })
      );
      return { ok: true, data: response.data };
    } catch (e) {
      const errBody = e?.response?.data;
      if (e?.response?.status === 403) {
        return { ok: false, blocked: true, error: errBody, code: errBody?.code };
      }
      return { ok: false, error: errBody || e.message };
    }
  };
};

/**
 * Lists Stripe Products + their active Prices for the order-form line-item picker.
 * @param {object} [params]
 * @param {string} [params.currency]      ISO lower-case (e.g. `gbp`). Drops Prices in other currencies on the backend.
 * @param {number} [params.limit]         Default 20, capped at 50.
 * @param {string} [params.search]        Free-text search (Stripe Search Query Language).
 * @param {string} [params.stripeAccount] Optional `acct_…` for Connect catalogs.
 * @returns {Promise<{ ok: boolean, products?: object[], flowDisabled?: boolean, error?: any, message?: string }>}
 */
const getStripeProducts = (params = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const org = state.currentOrganisation;
    if (!org) return { ok: false, error: { message: 'No organisation' } };

    dispatch(setLoading('products', true));
    try {
      const qs = new URLSearchParams();
      if (params.currency) qs.set('currency', String(params.currency).toLowerCase());
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.search) qs.set('search', String(params.search));
      if (params.stripeAccount) qs.set('stripeAccount', String(params.stripeAccount));
      const path = '/my/admin/stripe/products' + (qs.toString() ? `?${qs.toString()}` : '');

      const response = await axios(
        await createSignedRequest('GET', apiURL + path, null, { Organisation: String(org) })
      );
      const products = response?.data?.products || [];
      dispatch({
        type: Types.PRODUCTS,
        payload: { currency: params.currency || null, products },
      });
      return { ok: true, products };
    } catch (e) {
      if (e?.response?.status === 404) {
        dispatch({
          type: Types.PRODUCTS,
          payload: { currency: params.currency || null, products: [], flowDisabled: true },
        });
        return { ok: true, products: [], flowDisabled: true };
      }
      const errBody = e?.response?.data;
      const msg = errBody?.message || errBody?.code || e.message;
      console.log('getStripeProducts Error:', e);
      return { ok: false, error: errBody, message: msg };
    } finally {
      dispatch(setLoading('products', false));
    }
  };
};

/**
 * Fetch the exact Consultant Pricing Schedule that will be snapshotted into a new master Agreement.
 * @param {string} currency Contract currency symbol.
 * @returns {Function}
 */
const getConsultantPricingSchedule = (currency = '£') => {
  return async (dispatch, getState) => {
    const state = getState();
    const org = state.currentOrganisation;
    if (!org) return null;
    try {
      const query = new URLSearchParams({ currency }).toString();
      const response = await axios(
        await createSignedRequest(
          'GET',
          `${apiURL}/my/agreements/consultant-pricing-schedule?${query}`,
          null,
          { Organisation: String(org) }
        )
      );
      return response.data?.schedule || null;
    } catch (e) {
      console.log('getConsultantPricingSchedule Error:', e);
      return null;
    }
  };
};

const clearAgreements = () => ({ type: Types.CLEAR });

/**
 * Platform Admin: list consultant self-signup applications awaiting Confirm/Withdraw.
 * @returns {Function}
 */
const listConsultantApplications = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const org = state.currentOrganisation;
    if (!org) return { ok: false, applications: [] };
    try {
      const response = await axios(
        await createSignedRequest('GET', `${apiURL}/my/admin/consultant-applications`, null, {
          Organisation: String(org),
        })
      );
      return { ok: true, applications: response.data?.applications || [] };
    } catch (e) {
      console.log('listConsultantApplications Error:', e);
      return { ok: false, applications: [], error: e?.response?.data?.error || e.message };
    }
  };
};

/**
 * Platform Admin: Confirm a pending consultant application (triggers provisioning).
 * @param {string} contractId
 * @returns {Function}
 */
const confirmConsultantApplication = (contractId) => {
  return async (dispatch, getState) => {
    const state = getState();
    const org = state.currentOrganisation;
    try {
      await axios(
        await createSignedRequest(
          'POST',
          `${apiURL}/my/admin/consultant-applications/${encodeURIComponent(contractId)}/confirm`,
          JSON.stringify({}),
          { Organisation: String(org) }
        )
      );
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.error || e.message };
    }
  };
};

/**
 * Platform Admin: Withdraw a pending consultant application.
 * @param {string} contractId
 * @param {string} [reason]
 * @returns {Function}
 */
const withdrawConsultantApplication = (contractId, reason) => {
  return async (dispatch, getState) => {
    const state = getState();
    const org = state.currentOrganisation;
    try {
      await axios(
        await createSignedRequest(
          'POST',
          `${apiURL}/my/admin/consultant-applications/${encodeURIComponent(contractId)}/withdraw`,
          JSON.stringify({ reason: reason || undefined }),
          { Organisation: String(org) }
        )
      );
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.error || e.message };
    }
  };
};

/**
 * Public consultant self-signup (unsigned).
 * @param {object} body
 * @returns {Function}
 */
const createConsultantSelfSignup = (body) => {
  return async () => {
    try {
      const res = await createUnsignedRequest(
        'POST',
        `${apiURL}/public/consultant-signup`,
        JSON.stringify(body),
        {}
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        return { ok: false, error: data?.error || data?.code || `signup_failed_${res.status}` };
      }
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  };
};

const Actions = {
  getMyAgreements,
  getAgreementContract,
  createAgreement,
  resendAgreement,
  voidAgreement,
  getAgreementSignedPdfUrl,
  requestSubscriptionCancel,
  getStripeProducts,
  getConsultantPricingSchedule,
  listConsultantApplications,
  confirmConsultantApplication,
  withdrawConsultantApplication,
  createConsultantSelfSignup,
  clearAgreements,
};

export { Actions, Types };
